import { BulletPool } from './BulletPool.js';
import { DESPERATION_DURATION, GRAZE } from '../../../config/constants.js';
import { tagOverlap } from '../../data/patternTags.js';
import { applyVariant, unlockedVariant } from '../../data/patternVariants.js';

const PICK_COOLDOWN_BASE = 1.4; // seconds between pattern picks at phase 0, before a boss's own multiplier
const INITIAL_DELAY = 0.8; // grace period before the boss's first move
// §B5 — weight a candidate is multiplied by per tag it shares with a card
// already running. One shared tag makes a redundant pair a third as likely as
// a fresh question; two make it a ninth. Never zero: "two rings at once" is
// occasionally the right answer, and a ban would need the same
// everything-excluded fallback the arena-control rule needed.
const TAG_OVERLAP_PENALTY = 1 / 3;

/**
 * The one-card library card practice runs on (BULLET-HELL.md §D8). Searches
 * everything a def can own — its normal pool plus the two conditional cards,
 * the phase-2 unlock and the desperation finale — because those two are
 * exactly the cards a player has met least and would most want to drill, and
 * in a normal fight they are gated behind reaching low HP.
 *
 * `minPhase` is zeroed on the match for the same reason: the practice boss
 * never takes damage, so it never leaves phase 0, and a phase-gated card would
 * otherwise sit unpickable forever and leave the player in an empty arena.
 *
 * An unknown name falls back to the full library rather than to nothing — the
 * menu builds its names from data/patternCatalog.js, the same instantiate-and-
 * read-names call, so a miss means a bug somewhere else, and an ordinary fight
 * is a much better failure mode than a boss that never attacks.
 */
function practiceLibrary(bossDef, name) {
  const all = bossDef.createPatterns();
  if (bossDef.createPhase2Pattern) all.push(bossDef.createPhase2Pattern());
  if (bossDef.createDesperationPattern) all.push(bossDef.createDesperationPattern());
  const matches = all.filter((pattern) => pattern.name === name);
  if (!matches.length) return bossDef.createPatterns();
  for (const pattern of matches) pattern.minPhase = 0;
  return matches;
}

/**
 * Directs the boss's attack patterns: picks what runs next from the pool
 * unlocked at the current phase, avoids immediate repeats, and — once the
 * boss reaches its `layerFromPhase` — lets multiple patterns run
 * concurrently instead of one at a time. This is the "attack layering"
 * escalation described in the design doc: lower HP doesn't just mean new
 * moves, it means old and new moves stacking on top of each other. Later
 * bosses in the gauntlet (data/bosses.js) set a lower `layerFromPhase` and
 * a smaller `pickCooldownMultiplier` so they layer sooner and pick faster.
 *
 * Also owns the boss bullet pool and player-vs-bullet collision, mirroring
 * how combat/CombatSystem owns the player projectile pool and
 * projectile-vs-boss collision.
 */
export class SpawningSystem {
  /**
   * `practicePattern` (BULLET-HELL.md §D8) narrows the library to the one card
   * of that name, so the scheduler plays it, and only it, on repeat. Everything
   * else about the fight is unchanged — the same pattern object, the same
   * telegraph, the same capture accounting — because a card practised under
   * special-cased rules isn't the card you have to beat in the run.
   */
  constructor(eventBus, bossDef, combat = null, {
    practicePattern = null, rng = Math.random, patternRecords = null,
  } = {}) {
    this.eventBus = eventBus;
    this.pool = new BulletPool();
    this._bossDef = bossDef;
    this._combat = combat;
    this._practicePattern = practicePattern;
    // The run's card stream on a seeded run (BULLET-HELL.md §D8) — every boss's
    // scheduler in the run shares it, so the whole run's card order is one
    // sequence rather than each fight replaying the same one from its start.
    // Only the PICK below reads it; a pattern's own internal jitter stays on
    // Math.random, which is what keeps a seed a shared deck and not a replay.
    this._rng = rng;
    // §B10 — the player's lifetime capture record (SaveManager's
    // statistics.patternRecords). Null everywhere it isn't passed, which means
    // no variants, which is the right answer for a bench or a test.
    this._patternRecords = patternRecords;
    this._library = practicePattern
      ? practiceLibrary(bossDef, practicePattern)
      : bossDef.createPatterns();
    for (const pattern of this._library) this._inject(pattern);
    // §B1 — the authored opening of each phase. `script[phase]` is a list of
    // card names played in order the first time the boss reaches that phase;
    // anything past the end of a phase's list (or any phase without one) falls
    // through to the random picker, which is deliberate: §B1's "what not to
    // take" is that a fully scripted fight becomes a memorisation test on the
    // second clear. `_scriptCursor` is per phase, so re-entering a phase never
    // replays its opening.
    this._script = bossDef.script ?? null;
    this._scriptCursor = new Map();
    this._layerFromPhase = bossDef.layerFromPhase;
    this._pickCooldownMultiplier = bossDef.pickCooldownMultiplier;
    this._running = [];
    this._pickTimer = INITIAL_DELAY;
    this._lastPatternName = null;
    this._desperationPattern = null;
    // Desperation finale (see _updateDesperation): a fixed DESPERATION_DURATION
    // survival window rather than a single one-shot pattern. `null` until the
    // boss hits 0 HP and the window begins.
    this._desperationTimer = null;
    // Only ever true for bosses whose def has createPhase2Pattern (currently
    // just The Unmade King) — every other boss's bossDef simply lacks the
    // method, so this check is permanently a no-op for them.
    this._phase2Unlocked = false;
    // Spell-card bookkeeping (BULLET-HELL.md §D1). One entry per card
    // currently in flight, keyed by a per-card id rather than by pattern name
    // because two bosses can be up at once (heralds, Act III's pairs) running
    // the same-named pattern through two SpawningSystems on one event bus.
    // Holds the pool's fire count at begin(), so the end event can report how
    // many bullets THIS run of the card fired. Capture itself is judged
    // elsewhere (CaptureTracker) — this system reports card boundaries and
    // danger, and stays ignorant of payouts.
    this._cards = new Map();
    this._nextCardId = 1;
    this._boss = null;
    this._player = null;
    // §B6 — damage dealt under each breakable card in flight (pattern -> total).
    this._breakProgress = new Map();
  }

  /**
   * Hands a pattern the run-wide references it declared a slot for. Patterns
   * opt in by initialising the field to null in their constructor, so a pattern
   * that doesn't want a CombatSystem never sees one. Act III's Understudy (and
   * the two finale composites that own one) are the current consumers — they
   * fire the player's live loadout back, which means reading it.
   */
  _inject(pattern) {
    if ('combat' in pattern && !pattern.combat) pattern.combat = this._combat;
    // §B8 — telegraph rhythm as a per-boss identity axis. The library was
    // effectively all 0.6–1.3s wind-ups, so every fight in the game shared a
    // tempo. A def's `telegraphScale` bends its whole roster at once: the Iron
    // Cadence (quantized time) reads tight and regular, the Famished Court
    // slow and dreadful, without either needing a bespoke card.
    //
    // The pricing model already supports this: _endCard floors telegraph at
    // 0.4 in the danger proxy specifically so a short telegraph is priced as
    // *harder to read* rather than *cheaper to dodge*.
    const scale = this._bossDef.telegraphScale;
    if (scale && scale !== 1 && !pattern._telegraphScaled) {
      pattern.telegraphDuration *= scale;
      pattern._telegraphScaled = true;
    }
    // §B6 — a def names which of its cards can be BROKEN and what fraction of
    // its own HP pool that costs, so one line scales correctly across a
    // gauntlet whose HP spans 1520 to 65600. Written here rather than into
    // each pattern class because whether a card is breakable is a property of
    // the FIGHT (a boss's signature is worth attacking), not of the geometry.
    const cardBreak = this._bossDef.cardBreak;
    if (cardBreak?.cards?.includes(pattern.name)) {
      pattern.cardBreakDamage = this._bossDef.maxHp * cardBreak.fraction;
    }
    // §B10 — the harder form of a card the player has proven they can capture.
    // Applied at library build, once, so the fight is the same fight from its
    // first second to its last.
    const variant = unlockedVariant(pattern.name, this._patternRecords);
    if (variant) applyVariant(pattern, variant);
  }

  update(dt, boss, player) {
    // Retained for creditDamage (§B6), which is driven by the combat system's
    // hits rather than by this tick and still has to hand a broken card's
    // onEnd the same arguments a timed-out card gets.
    this._boss = boss;
    this._player = player;
    this.pool.update(dt);
    this._checkPlayerCollisions(player);

    if (boss.isDying) {
      this._updateDesperation(dt, boss, player);
      return;
    }

    // Phase 2 threshold (boss.phase reaches 2 at <=50% HP, same threshold
    // config/constants.js's BOSS.phaseThresholds already defines) — the new
    // pattern joins the pool permanently and becomes pickable like any other.
    // Never in card practice: the library there is deliberately one card, and
    // the phase-2 unlock joining it would quietly turn a drill into a fight.
    if (!this._practicePattern && !this._phase2Unlocked && boss.phase >= 2 && this._bossDef.createPhase2Pattern) {
      this._phase2Unlocked = true;
      const phase2 = this._bossDef.createPhase2Pattern();
      this._inject(phase2);
      this._library.push(phase2);
    }

    for (const pattern of this._running) {
      this.pool.sourceLabel = pattern.name;
      this.pool.spawnOverlay = this._overlayFor(pattern);
      pattern.update(dt, boss, player, this.pool);
    }
    this.pool.sourceLabel = null;
    this.pool.spawnOverlay = null;
    for (const pattern of this._running) {
      if (pattern.isDone) this._endCard(pattern);
    }
    this._running = this._running.filter((pattern) => !pattern.isDone);

    // Past the phase-2 unlock (currently only ever true for The Unmade
    // King), the "true form" layers one pattern harsher than anything else
    // in the game, on top of the normal layerFromPhase gate below.
    const layered = boss.phase >= this._layerFromPhase;
    const maxConcurrent = this._phase2Unlocked ? 3 : (layered ? 2 : 1);
    if (this._running.length < maxConcurrent) {
      this._pickTimer -= dt;
      if (this._pickTimer <= 0) this._startNextPattern(boss, player);
    }
  }

  // The desperation finale hands control to the boss's bespoke sequence for
  // the fixed survival window. Most bosses finish immediately; the King owns
  // one continuous all-out pattern rather than returning to the normal picker.
  _updateDesperation(dt, boss, player) {
    // Bosses whose def lacks a desperation finale (every boss but The Unmade
    // King) die immediately instead of entering the survival window.
    if (!this._bossDef.createDesperationPattern) {
      // Whatever was mid-flight when the boss fell is neither survived nor
      // failed — the fight ended under it.
      this.abandonCards();
      boss.finishDying();
      return;
    }

    if (this._desperationTimer === null) {
      this._desperationTimer = DESPERATION_DURATION;
      this._running = [];
      this.abandonCards();
      // Clearing _running skips Hushfall's onEnd, so drop any lingering hush
      // mute defensively — desperation is survival-only, weapons must work.
      player.muted = false;
      this._desperationPattern = this._bossDef.createDesperationPattern();
      this._inject(this._desperationPattern);
      this.pool.sourceLabel = this._desperationPattern.name;
      // Opened BEFORE begin() so the card's fire baseline predates its first
      // bullet. The whole window is one card, even though the pattern itself
      // is restarted below whenever it runs out early.
      this._beginCard(this._desperationPattern, { desperation: true });
      this._desperationPattern.begin(boss, player);
      this.pool.sourceLabel = null;
    }

    this._desperationTimer -= dt;
    this.eventBus.emit('desperation:tick', { remaining: Math.max(0, this._desperationTimer) });

    this.pool.sourceLabel = this._desperationPattern?.name ?? null;
    if (this._desperationPattern && !this._desperationPattern.isDone) {
      this._desperationPattern.update(dt, boss, player, this.pool);
    }
    if (this._desperationPattern?.isDone && this._desperationTimer > 0) {
      this._desperationPattern.begin(boss, player);
    }
    this.pool.sourceLabel = null;

    if (this._desperationTimer <= 0) {
      // The boss's last word resolves as a card like any other — reaching the
      // end of the window IS surviving it, so this is always an end and never
      // an abandon.
      if (this._desperationPattern) this._endCard(this._desperationPattern);
      // Survived the full window — a distinct beat from the boss:died that
      // finishDying emits: "you made it" vs. "it's finally dead."
      this.eventBus.emit('desperation:end');
      boss.finishDying();
    }
  }

  _startNextPattern(boss, player) {
    const unlocked = this._library.filter(
      (p) => p.minPhase <= boss.phase && !this._running.includes(p),
    );
    if (!unlocked.length) return;

    // A bullet pattern may layer over one arena-control rule, but two
    // independent movement rules can create an impossible resolution (for
    // example Grid Barrage + Throne Divide, or Undertow + Vow). Prefer a
    // non-control candidate whenever one exists. If a boss's entire unlocked
    // pool is control patterns, the fallback still permits one pattern to run
    // rather than stalling the scheduler.
    const hasArenaControl = this._running.some((p) => p.arenaControl);
    const eligible = hasArenaControl
      ? (unlocked.filter((p) => !p.arenaControl).length
        ? unlocked.filter((p) => !p.arenaControl)
        : unlocked)
      : unlocked;

    const notRepeated = eligible.filter((p) => p.name !== this._lastPatternName);
    const candidates = notRepeated.length ? notRepeated : eligible;
    // §B1 — an authored opening, if this phase still has one and the card it
    // names is legal right now. A scripted card that is already running, still
    // phase-locked or excluded by the arena-control rule is SKIPPED rather
    // than forced: the exclusion exists because two forced-position rules can
    // be unresolvable, and an author should not be able to write that.
    const chosen = this._scriptedPick(candidates, boss) ?? this._weightedPick(candidates);

    // onBegin can fire bullets immediately (not every pattern waits for its
    // active window), so the stamp has to be set here too, not only in update.
    // §B7 — a toned boss alternates the tone CARD BY CARD, so polarity arrives
    // as a block the player routes through rather than as a per-bullet coin
    // flip. Decided at pick time and held for the card's whole run.
    if (this._bossDef.tonedCards) {
      this._cardTone = this._cardTone === 'warm' ? 'cold' : 'warm';
    }
    this.pool.sourceLabel = chosen.name;
    this.pool.spawnOverlay = this._overlayFor(chosen);
    chosen.begin(boss, player);
    this.pool.sourceLabel = null;
    this.pool.spawnOverlay = null;
    this._running.push(chosen);
    this._lastPatternName = chosen.name;
    this._beginCard(chosen);

    // §B6 — the card's damage budget. A signature card with `cardBreakDamage`
    // can be ENDED EARLY by the player's own output while it runs, which is
    // what turns capture from something that happens to you into something you
    // can attack. Tracked per running card rather than per pattern: two
    // layered cards each own their own slice of the damage dealt under them.
    if (chosen.cardBreakDamage > 0) this._breakProgress.set(chosen, 0);

    const speedup = Math.max(0.4, 1 - boss.phase * 0.18) * this._pickCooldownMultiplier;
    // Gilded Pact's cost: >1 shortens the gap between picks (1.15 = 15% faster).
    this._pickTimer = PICK_COOLDOWN_BASE * speedup / (player.bossPickSpeedMultiplier ?? 1);
  }

  /**
   * The spawn patch for whatever `pattern` is about to fire: the §B10 variant's
   * motion, plus §B7's card tone if this boss is a toned one.
   *
   * `tonedCards` on a def is the ATTACK_CONFIG-level axis §B7 asks for — any
   * boss in any act can opt into polarity without owning a bespoke toned card,
   * which is what un-strands Tine (and the whole resonance economy) from the
   * one Act II finale card that used to be the only toned source in the game.
   * A pattern that tones its own bullets (Chorus of the Deep, Twin Voice)
   * passes `tone` at fire time and the overlay would overwrite it — so a card
   * that already speaks polarity is left alone.
   */
  _overlayFor(pattern) {
    const variantOverlay = pattern.bulletOverlay ?? null;
    const toned = this._bossDef.tonedCards;
    if (!toned || pattern.tonesOwnBullets) return variantOverlay;
    return {
      ...(variantOverlay ?? {}),
      tone: this._cardTone ?? 'warm',
      mismatchBonus: toned.mismatchBonus ?? 0,
    };
  }

  /**
   * The next card of this phase's authored script (BOSS-REWORK.md §B1), or
   * null to hand back to the picker.
   *
   * Consumes the cursor only when the named card is actually played, so a
   * script entry naming a card that is currently illegal is deferred rather
   * than lost — the opening still arrives in order, one beat later.
   */
  _scriptedPick(candidates, boss) {
    if (!this._script || this._practicePattern) return null;
    const phaseScript = this._script[boss.phase];
    if (!phaseScript) return null;
    const cursor = this._scriptCursor.get(boss.phase) ?? 0;
    if (cursor >= phaseScript.length) return null;
    const wanted = phaseScript[cursor];
    const match = candidates.find((p) => p.name === wanted);
    if (!match) return null;
    this._scriptCursor.set(boss.phase, cursor + 1);
    return match;
  }

  /**
   * Uniform random, less §B5's redundancy penalty: a candidate that asks the
   * same question as something already on screen (two rings, two beams, two
   * grids) is weighted down rather than excluded. With nothing running — the
   * unlayered case, which is most of most fights — every weight is 1 and this
   * is exactly the old uniform pick.
   */
  _weightedPick(candidates) {
    const runningNames = this._running.map((p) => p.name);
    let total = 0;
    const weights = candidates.map((p) => {
      const weight = TAG_OVERLAP_PENALTY ** tagOverlap(p.name, runningNames);
      total += weight;
      return weight;
    });
    let roll = this._rng() * total;
    for (let i = 0; i < candidates.length; i++) {
      roll -= weights[i];
      if (roll <= 0) return candidates[i];
    }
    return candidates[candidates.length - 1];
  }

  /**
   * Card HP break (BOSS-REWORK.md §B6). Damage the player deals while a card
   * runs is credited to every card in flight, and a card whose
   * `cardBreakDamage` budget is met ENDS EARLY — counted as a capture by the
   * ordinary end path, because breaking a card is the loudest possible way of
   * surviving it.
   *
   * Deliberately NOT a per-card HP subpool: that would need the boss to be
   * invulnerable outside cards, which invalidates every DPS build the game
   * sells. The boss's HP is still one pool and every point still lands on it —
   * this only decides when the bullets stop.
   */
  creditDamage(amount) {
    if (!amount || !this._breakProgress.size) return;
    for (const [pattern, dealt] of this._breakProgress) {
      const total = dealt + amount;
      this._breakProgress.set(pattern, total);
      if (total >= pattern.cardBreakDamage) this._breakCard(pattern);
    }
  }

  _breakCard(pattern) {
    this._breakProgress.delete(pattern);
    if (!this._running.includes(pattern)) return;
    // Through the pattern's own onEnd, never by dropping it: a card that owns
    // arena state (a wall, a hush, a lock on the boss) has to be allowed to
    // take it back down, exactly as a timed-out card does.
    // The frame's boss/player, kept by update(), because onEnd hooks take them
    // and creditDamage is called from combat rather than from the tick.
    pattern.forceEnd?.(this._boss, this._player, this.pool);
    this.eventBus.emit('boss:pattern-broken', { name: pattern.name });
  }

  // --- spell cards (BULLET-HELL.md §D1) ------------------------------------
  // `boss:pattern-start` is the announcement the telegraph callout has always
  // ridden; the -end/-abandon pair is new, and exists so a listener can tell
  // "the player survived this card" from "this card stopped existing". Every
  // card that starts resolves exactly once, through one or the other.

  _beginCard(pattern, { desperation = false } = {}) {
    const id = this._nextCardId++;
    this._cards.set(pattern, {
      id,
      firedAtBegin: this.pool.firedUnder(pattern.name),
      desperation,
    });
    this.eventBus.emit('boss:pattern-start', {
      id,
      name: pattern.name,
      telegraphDuration: pattern.telegraphDuration,
      desperation,
    });
  }

  _endCard(pattern) {
    const card = this._cards.get(pattern);
    this._breakProgress.delete(pattern); // §B6 — the budget dies with the card either way
    if (!card) return;
    this._cards.delete(pattern);
    const bulletsFired = this.pool.firedUnder(pattern.name) - card.firedAtBegin;
    this.eventBus.emit('boss:pattern-end', {
      id: card.id,
      name: pattern.name,
      desperation: card.desperation,
      bulletsFired,
      // The danger proxy BULLET-HELL.md §D1 specifies. Telegraph length is
      // floored so a near-instant card still prices its bullets: a short
      // telegraph makes an attack harder to read, not cheaper to dodge.
      danger: Math.max(pattern.telegraphDuration, 0.4) * bulletsFired,
    });
  }

  /**
   * Every in-flight card stops being judged: it was neither survived nor
   * failed, so it must count as neither. Used where the scheduler drops
   * patterns out from under the player rather than letting them finish — the
   * desperation hand-off, and the boss dying mid-card.
   */
  abandonCards() {
    for (const [, card] of this._cards) {
      this.eventBus.emit('boss:pattern-abandon', { id: card.id });
    }
    this._cards.clear();
    this._breakProgress.clear();
  }

  _checkPlayerCollisions(player) {
    this.pool.forEachActive((bullet) => {
      const dist = Math.hypot(bullet.x - player.x, bullet.y - player.y);
      const hitDist = bullet.radius + player.radius;
      // Graze: a near-miss inside the band — or hull contact during dash
      // i-frames (takeDamage below will no-op, so the bullet is absorbed
      // without damage exactly as before; the graze is the reward for
      // dashing through it). Plain hull hits and Phase Step dodges never
      // graze — a dodge is luck, a graze is positioning.
      //
      // The payout is DEFERRED to the frame the bullet leaves the band. Every
      // bullet that hits you crosses the band on its way in, so paying on
      // entry meant a hit always came with a free graze — the meter climbing
      // while the player was being hit. Now entering only arms it, and the
      // hull check below disarms it.
      //
      // Nothing arms during the post-hit mercy window either: bullets wash
      // through unconsumed for those 0.4s (see the hull branch), which turned
      // standing inside a curtain right after a hit into free income.
      const inBand = dist < hitDist + GRAZE.band;
      if (!bullet.grazed && !bullet.grazePending && inBand
        && !player.isHitInvulnerable
        && (dist >= hitDist || player.isInvincible)) {
        bullet.grazePending = true;
      } else if (bullet.grazePending && !inBand) {
        bullet.grazePending = false;
        bullet.grazed = true;
        this.eventBus.emit('bullet:grazed', { x: bullet.x, y: bullet.y });
      }
      if (dist < hitDist) {
        // Antiphon polarity: a bullet whose tone matches the player's
        // attunement washes through harmlessly (and keeps flying — not
        // consumed); the opposite tone bites for +mismatchBonus. Untoned
        // bullets (tone null) are unaffected and hit as normal.
        // Unison (Tine's ultimate) makes every toned bullet a matched one for
        // its window — the champion's own mechanic turned all the way up, so
        // it pays income (charge + releases) rather than simply granting
        // immunity. Untoned bullets still hit: her signature has already toned
        // everything this pool fired, so the ones left untoned in her runs are
        // the ones no attunement was ever an answer to.
        if (bullet.tone && (player.unison || bullet.tone === player.attunement)) {
          // BULLET-HELL.md §D7 — and absorbing PAYS: resonance charges the
          // ultimate meter and, every POLARITY.stacksPerRelease, discharges as
          // a homing volley (Resonance + Game._firePolarityRelease). Latched
          // per bullet because the bullet keeps flying, so an absorb is one
          // event per bullet rather than one per overlapping tick.
          if (!bullet.absorbed) {
            bullet.absorbed = true;
            this.eventBus.emit('bullet:absorbed', { x: bullet.x, y: bullet.y, tone: bullet.tone });
          }
          return;
        }
        // The deferred graze (above) is settled here: a bullet that reaches
        // the hull pays nothing, because it hit. The exception is dash
        // i-frames, where the hull contact IS the reward — and where the
        // bullet is consumed below, so exiting the band will never settle it.
        if (bullet.grazePending) {
          bullet.grazePending = false;
          if (player.isInvincible) {
            bullet.grazed = true;
            this.eventBus.emit('bullet:grazed', { x: bullet.x, y: bullet.y });
          }
        }
        // Post-hit mercy window (Player._hitInvulnTimer): the bullet washes
        // through rather than being consumed. The window's whole safety job is
        // the 0.4s rate limit on incoming hits — absorbing on top of that did
        // no additional work (a wall deals one hit per window either way) and
        // handed out a free screen clear for having been hit, which both
        // rewards the failure state and hides the pattern that caused it.
        // Dash i-frames still absorb: that read is earned and costs a charge,
        // the same asymmetry the graze rule above already draws. Checked
        // together so a dash begun inside the mercy window still absorbs.
        if (player.isHitInvulnerable && !player.isInvincible) return;
        const toneMult = bullet.tone ? 1 + (bullet.mismatchBonus ?? 0) : 1;
        player.takeDamage(bullet.damage * toneMult, bullet.source);
        bullet.active = false;
      }
    });
  }

  /** Visual descriptors (telegraphs + active beams/shields) for rendering. */
  getVisuals(boss) {
    const visuals = [];
    // A pattern that drives child patterns (Act III's two finale composites)
    // returns their visuals wrapped in one `composite` descriptor. Flattened
    // here so the renderer only ever sees the real kinds and never has to know
    // that a pattern can own patterns.
    const push = (v) => {
      if (!v) return;
      if (v.kind === 'composite') visuals.push(...v.children);
      else visuals.push(v);
    };
    if (boss.isDying) push(this._desperationPattern?.getVisual(boss));
    // Library patterns now also run during desperation (see
    // _updateDesperation), so their telegraphs need to render too — not
    // just the bespoke finisher's.
    for (const pattern of this._running) push(pattern.getVisual(boss));
    return visuals;
  }

  forEachBullet(fn) {
    this.pool.forEachActive(fn);
  }
}
